import React, { useState } from 'react';
import { Button, Stack } from '@mui/material';
import e2iApi from '../../services/e2iApi';
import type { Cir } from '../../types/e2i';
import { draftLabel, errorText, todayIso } from '../../utils/e2i';
import ActionDialog from './ActionDialog';
import type { Notify } from './workspace';

type CirAction = 'book' | 'return' | 'cancel' | 'client';

interface Props {
  cir: Cir;
  /** Shows the request the action answered with (and reloads what depends on it). */
  onChanged: (cir: Cir) => Promise<void> | void;
  /** Edit: the dialog in the workspace; the checklist opens the enquiry instead. */
  onEdit?: () => void;
  editLabel?: string;
  notify: Notify;
  size?: 'small' | 'medium';
}

/** What the user may do with an invoice request now: edit, submit, book or return (finance), cancel, client date. */
const CirActionBar: React.FC<Props> = ({ cir, onChanged, onEdit, editLabel = 'Edit', notify, size = 'small' }) => {
  const [action, setAction] = useState<CirAction | null>(null);
  const [busy, setBusy] = useState(false);
  const p = cir.permissions || ({} as Cir['permissions']);
  const no = cir.cirNo || 'The invoice request';

  const submit = async () => {
    setBusy(true);
    try {
      const next = await e2iApi.submitCir(cir.cirId);
      await onChanged(next);
      notify(`${no} was submitted to finance`, 'success');
    } catch (e: any) {
      notify(errorText(e, 'The invoice request could not be submitted'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async ({ comment, date }: { comment: string; date: string | null }) => {
    if (!action) return;
    let next: Cir;
    if (action === 'book') next = await e2iApi.bookCir(cir.cirId);
    else if (action === 'return') next = await e2iApi.returnCir(cir.cirId, comment);
    else if (action === 'cancel') next = await e2iApi.cancelCir(cir.cirId, comment);
    else next = await e2iApi.clientSubmission(cir.cirId, date || todayIso());
    setAction(null);
    await onChanged(next);
    if (action === 'book') {
      if (next.sap?.error && next.status !== 'Booked') notify(`SAP refused the invoice draft: ${next.sap.error}`, 'error');
      else notify(`${no} is booked: SAP A/R invoice draft ${draftLabel(next.sap?.draftNum, next.sap?.draftEntry)} waits for finance to add it`.trim(), 'success');
    } else if (action === 'return') notify(`${no} was returned to the team`, 'success');
    else if (action === 'cancel') notify(`${no} was cancelled`, 'success');
    else notify(`Client submission date of ${no} saved`, 'success');
  };

  const dialogs: Record<CirAction, { title: string; help: string; button: string; comment?: string; date?: string; danger?: boolean }> = {
    book: {
      title: `Book ${no}`,
      help: 'PRPO checks the customer, its VAT number and the sales order lines in SAP, then makes the SAP A/R invoice as a DRAFT from the order lines. Add it in SAP through the approval procedure; PRPO sees the invoice within a few minutes.',
      button: cir.sap?.error ? 'Book again' : 'Book as SAP draft',
    },
    return: {
      title: `Return ${no}`,
      help: cir.status === 'Booked'
        ? 'The request goes back to the team. PRPO does not delete anything in SAP: remove the A/R invoice draft in SAP yourself.'
        : 'The request goes back to the team, who can correct it and submit it again.',
      button: 'Return',
      comment: 'What to correct',
    },
    cancel: { title: `Cancel ${no}`, help: 'Its job lines become free for another invoice request.', button: 'Cancel request', comment: 'Reason', danger: true },
    client: { title: `Client submission of ${no}`, help: 'The day the invoice was handed or sent to the client.', button: 'Save date', date: 'Submitted to the client on' },
  };
  const d = action ? dialogs[action] : null;

  return (
    <>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {p.canEdit && onEdit ? <Button size={size} variant="outlined" onClick={onEdit} disabled={busy}>{editLabel}</Button> : null}
        {p.canSubmit ? <Button size={size} onClick={() => { void submit(); }} disabled={busy}>{busy ? 'Submitting…' : 'Submit to finance'}</Button> : null}
        {p.canBook ? <Button size={size} onClick={() => setAction('book')} disabled={busy}>{cir.sap?.error ? 'Book again' : 'Book'}</Button> : null}
        {p.canReturn ? <Button size={size} variant="outlined" onClick={() => setAction('return')} disabled={busy}>Return</Button> : null}
        {p.canSetClientDate ? (
          <Button size={size} variant="outlined" onClick={() => setAction('client')} disabled={busy}>{cir.clientSubmissionDate ? 'Change client date' : 'Client submission date'}</Button>
        ) : null}
        {p.canCancel ? <Button size={size} variant="outlined" color="error" onClick={() => setAction('cancel')} disabled={busy}>Cancel</Button> : null}
      </Stack>
      <ActionDialog
        open={!!action}
        id={`e2i-cir-${cir.cirId}-action`}
        title={d?.title || ''}
        help={d?.help}
        confirmLabel={d?.button || 'OK'}
        danger={d?.danger}
        commentLabel={d?.comment}
        commentRequired={!!d?.comment}
        dateLabel={d?.date}
        dateRequired={!!d?.date}
        initialDate={action === 'client' ? (cir.clientSubmissionDate ? cir.clientSubmissionDate.slice(0, 10) : todayIso()) : undefined}
        maxDate={action === 'client' ? todayIso() : undefined}
        onClose={() => setAction(null)}
        onConfirm={confirm}
      />
    </>
  );
};

export default CirActionBar;
